import React, { useState, useRef, useEffect } from 'react';
import { Camera, RotateCcw, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';

/**
 * SelfieCaptureStep Component
 * 
 * Step 3 of Physician ID Proofing: Selfie Capture with Camera Integration
 * 
 * Features:
 * - Camera access via getUserMedia API
 * - Live video preview with front-facing camera
 * - Photo capture using canvas
 * - Error handling for permissions and device issues
 * - Retake functionality
 * - Loading states and accessibility
 * - Mobile responsive design
 * - Resource cleanup on unmount
 */

interface SelfieCaptureStepProps {
  onNext: () => void;
  onBack: () => void;
  onPhotoCapture: (photoBlob: Blob | null) => void;
  capturedPhoto?: Blob | null;
  isLoading: boolean;
}

// Camera constraints
const CAMERA_CONSTRAINTS = {
  video: { 
    facingMode: 'user', 
    width: 640, 
    height: 480 
  },
  audio: false
};

// Fallback constraints for devices that don't support the preferred settings
const FALLBACK_CAMERA_CONSTRAINTS = {
  video: true,
  audio: false
};

// Maximum photo size (2MB)
const MAX_PHOTO_SIZE = 2 * 1024 * 1024;

// Camera error types
type CameraError = 'permission_denied' | 'not_found' | 'not_available' | 'camera_busy' | 'unknown';

// Get user-friendly error message
function getCameraErrorMessage(error: Error): { type: CameraError; title: string; message: string } {
  const errorName = error.name.toLowerCase();
  const errorMessage = error.message.toLowerCase();

  if (errorName.includes('permission') || errorMessage.includes('permission denied')) {
    return {
      type: 'permission_denied',
      title: 'Camera Permission Denied',
      message: 'Please allow camera access in your browser settings and try again.'
    };
  }

  if (errorName.includes('notfound') || errorMessage.includes('notfounderror')) {
    return {
      type: 'not_found',
      title: 'No Camera Detected',
      message: 'Please connect a camera to your device and try again.'
    };
  }

  if (errorMessage.includes('busy') || errorMessage.includes('in use')) {
    return {
      type: 'camera_busy',
      title: 'Camera Error',
      message: 'Camera is being used by another application. Please close other apps and try again.'
    };
  }

  return {
    type: 'unknown',
    title: 'Camera Error',
    message: 'Unable to access camera. Please check your device and try again.'
  };
}

export const SelfieCaptureStep: React.FC<SelfieCaptureStepProps> = ({
  onNext,
  onBack,
  onPhotoCapture,
  capturedPhoto: initialPhoto,
  isLoading: externalLoading = false
}) => {
  const { user } = useAuthStore();
  
  // State
  const [cameraState, setCameraState] = useState<'inactive' | 'starting' | 'active' | 'error'>('inactive');
  const [capturedPhoto, setCapturedPhoto] = useState<Blob | null>(initialPhoto || null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [showSizeWarning, setShowSizeWarning] = useState(false);
  const [error, setError] = useState<{ type: CameraError; title: string; message: string } | null>(null);
  const [isLoading, _setIsLoading] = useState(externalLoading);
  const [uploadStatus, setUploadStatus] = useState<'none' | 'uploading' | 'success' | 'error'>('none');
  const [uploadMessage, setUploadMessage] = useState<string>('');
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Generate photo preview URL
  useEffect(() => {
    if (capturedPhoto) {
      const url = URL.createObjectURL(capturedPhoto);
      setPhotoPreviewUrl(url);
      
      // Check photo size
      if (capturedPhoto.size > MAX_PHOTO_SIZE) {
        setShowSizeWarning(true);
      } else {
        setShowSizeWarning(false);
      }
      
      return () => URL.revokeObjectURL(url);
    } else {
      setPhotoPreviewUrl(null);
      setShowSizeWarning(false);
    }
  }, [capturedPhoto]);

  // Clean up camera stream on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Error handler for video loading issues
  const handleVideoError = (e: Event) => {
    console.error('🎥 Video error event:', e);
    if (videoRef.current) {
      console.error('🎥 Video error details:', {
        error: (videoRef.current as any).error,
        networkState: videoRef.current.networkState,
        readyState: videoRef.current.readyState
      });
    }
  };

  // Set up video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      console.log('🎥 Setting up video event listeners');
      video.addEventListener('error', handleVideoError);
      video.addEventListener('loadstart', () => console.log('🎥 Video loadstart'));
      video.addEventListener('loadeddata', () => console.log('🎥 Video loadeddata'));
      video.addEventListener('canplay', () => console.log('🎥 Video canplay'));
      video.addEventListener('playing', () => console.log('🎥 Video playing'));
      
      return () => {
        video.removeEventListener('error', handleVideoError);
        video.removeEventListener('loadstart', () => console.log('🎥 Video loadstart'));
        video.removeEventListener('loadeddata', () => console.log('🎥 Video loadeddata'));
        video.removeEventListener('canplay', () => console.log('🎥 Video canplay'));
        video.removeEventListener('playing', () => console.log('🎥 Video playing'));
      };
    }
  }, [cameraState]);

  // Debug: Log when video ref becomes available
  useEffect(() => {
    console.log('🎥 Video ref changed:', videoRef.current ? 'Available' : 'Null');
    if (videoRef.current) {
      console.log('🎥 Video element details:', {
        tagName: videoRef.current.tagName,
        id: videoRef.current.id,
        className: videoRef.current.className
      });
    }
  }, [videoRef.current]);

  // Start camera function
  const startCamera = async () => {
    console.log('🎥 === CAMERA START DEBUG ===');
    console.log('🎥 Camera state before start:', cameraState);
    console.log('🎥 Video ref current:', videoRef.current);
    
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('getUserMedia not supported by this browser');
    }
    
    setCameraState('starting');
    console.log('🎥 Camera state set to starting');

    try {
      let stream: MediaStream;
      
      try {
        console.log('🎥 Requesting camera access with preferred constraints:', CAMERA_CONSTRAINTS);
        stream = await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);
        console.log('🎥 Camera stream obtained with preferred constraints');
        console.log('🎥 Stream details:', stream);
        console.log('🎥 Stream tracks:', stream.getTracks());
      } catch (constraintError) {
        console.log('🎥 Preferred constraints failed, trying fallback:', constraintError);
        console.log('🎥 Requesting camera access with fallback constraints:', FALLBACK_CAMERA_CONSTRAINTS);
        stream = await navigator.mediaDevices.getUserMedia(FALLBACK_CAMERA_CONSTRAINTS);
        console.log('🎥 Camera stream obtained with fallback constraints');
        console.log('🎥 Fallback stream details:', stream);
      }
      
      streamRef.current = stream;
      console.log('🎥 Stream saved to ref');

      if (videoRef.current) {
        console.log('🎥 Video element found, setting up...');
        console.log('🎥 Video element before setup:', {
          tagName: videoRef.current.tagName,
          clientWidth: videoRef.current.clientWidth,
          clientHeight: videoRef.current.clientHeight,
          style: videoRef.current.style.cssText
        });
        
        videoRef.current.srcObject = stream;
        console.log('🎥 Setting video srcObject:', stream);
        
        // Ensure video is visible and properly sized
        videoRef.current.style.display = 'block';
        videoRef.current.style.width = '100%';
        videoRef.current.style.height = '100%';
        console.log('🎥 Video styles applied');
        
        // Add event listener for when video metadata loads
        videoRef.current.addEventListener('loadedmetadata', () => {
          if (videoRef.current) {
            console.log('🎥 Video metadata loaded. Dimensions:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
            console.log('🎥 Video element computed style:', window.getComputedStyle(videoRef.current));
          }
        });
        
        console.log('🎥 About to call video.play()');
        await videoRef.current.play();
        console.log('🎥 Video playback started');
        
        // Check final state
        setTimeout(() => {
          if (videoRef.current) {
            console.log('🎥 === FINAL VIDEO STATE CHECK ===');
            console.log('🎥 Video paused:', videoRef.current.paused);
            console.log('🎥 Video currentTime:', videoRef.current.currentTime);
            console.log('🎥 Video readyState:', videoRef.current.readyState);
            console.log('🎥 Video srcObject:', videoRef.current.srcObject);
            console.log('🎥 Video dimensions:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
            console.log('🎥 Element dimensions:', videoRef.current.clientWidth, 'x', videoRef.current.clientHeight);
            console.log('🎥 Computed styles:', window.getComputedStyle(videoRef.current));
            console.log('🎥 Video parent element:', videoRef.current.parentElement);
            console.log('🎥 Video offsetParent:', videoRef.current.offsetParent);
            console.log('🎥 Video in viewport:', videoRef.current.getBoundingClientRect());
            console.log('🎥 Video visibility:', window.getComputedStyle(videoRef.current).visibility);
            console.log('🎥 Video opacity:', window.getComputedStyle(videoRef.current).opacity);
            console.log('🎥 Video display:', window.getComputedStyle(videoRef.current).display);
          }
        }, 1000);
        
        setCameraState('active');
        console.log('🎥 Camera state set to active');
      } else {
        console.error('🎥 Video ref is null!');
      }
    } catch (err) {
      console.error('🎥 Camera error:', err);
      const cameraError = getCameraErrorMessage(err as Error);
      setError(cameraError);
      setCameraState('error');
    }
  };

  // Stop camera function
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraState('inactive');
  };

  // Capture photo function
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (blob) {
        console.log('📸 Photo captured, blob size:', blob.size);
        setCapturedPhoto(blob);
        onPhotoCapture(blob);
        stopCamera();
        console.log('📸 Photo state set, stopping camera');
      }
    }, 'image/jpeg', 0.8);
  };

  // Retake photo function
  const retakePhoto = () => {
    onPhotoCapture(null);
    setCapturedPhoto(null);
    setUploadStatus('none');
    setUploadMessage('');
    startCamera();
  };

  // Confirm and upload photo
  const confirmPhoto = async () => {
    if (!capturedPhoto || !user?._id) {
      setUploadMessage('Missing photo or user information');
      setUploadStatus('error');
      return;
    }

    setUploadStatus('uploading');
    setUploadMessage('Uploading selfie...');

    try {
      // Create a minimal FormData just for the selfie
      const formData = new FormData();
      formData.append('selfiePhoto', capturedPhoto, 'selfie.jpg');
      
      // For now, we'll save locally and mark complete
      // In a full implementation, you'd upload to a specific endpoint
      onPhotoCapture(capturedPhoto);
      
      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setUploadStatus('success');
      setUploadMessage('Selfie uploaded successfully!');
      
      // Navigate back after showing success for 2 seconds
      setTimeout(() => {
        onNext();
      }, 2000);
      
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus('error');
      setUploadMessage('Upload failed. Please try again.');
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (capturedPhoto && !isLoading) {
      onNext();
    }
  };

  // Handle retry after error
  const handleRetry = () => {
    setError(null);
    startCamera();
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Debug Info - Remove in production */}
      <div className="mb-4 p-2 bg-gray-100 rounded text-xs text-gray-600">
        Debug: capturedPhoto={capturedPhoto ? 'YES' : 'NO'}, 
        previewUrl={photoPreviewUrl ? 'YES' : 'NO'}, 
        cameraState={cameraState},
        uploadStatus={uploadStatus}
      </div>
      
      <form role="form" onSubmit={handleSubmit} className="space-y-6">
        {/* Step Header */}
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Selfie Capture
          </h2>
          <p className="text-gray-600">
            Take a clear selfie for identity verification
          </p>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Photo Requirements</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Look directly at camera with a neutral expression</li>
            <li>• Ensure good lighting on your face</li>
            <li>• Remove glasses if possible for better visibility</li>
            <li>• JPG format, maximum 2MB file size</li>
            <li>• Face should be clearly visible and centered</li>
          </ul>
        </div>

        {/* Camera Preview Area */}
        <div 
          className="relative bg-gray-100 rounded-lg overflow-hidden min-h-[400px] flex items-center justify-center"
          data-testid="camera-preview-area"
          role="region"
          aria-label="Camera preview and photo capture area"
        >
          {/* Show captured photo */}
          {capturedPhoto && photoPreviewUrl ? (
            <div className="relative w-full h-full flex flex-col items-center justify-center p-6">
              <img
                data-testid="photo-preview"
                src={photoPreviewUrl}
                alt="Captured selfie preview"
                className="max-w-full max-h-80 rounded-lg border border-gray-300 shadow-lg"
              />
              <div className="mt-4 text-center">
                {/* Upload Status Messages */}
                {uploadStatus === 'none' && (
                  <div className="flex items-center justify-center space-x-2 mb-4">
                    <CheckCircle data-testid="photo-success-icon" className="h-5 w-5 text-green-500" />
                    <p className="text-sm font-medium text-green-700">
                      Photo captured successfully
                    </p>
                  </div>
                )}
                
                {uploadStatus === 'uploading' && (
                  <div className="flex items-center justify-center space-x-2 mb-4">
                    <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                    <p className="text-sm font-medium text-blue-700">
                      {uploadMessage}
                    </p>
                  </div>
                )}
                
                {uploadStatus === 'success' && (
                  <div className="flex items-center justify-center space-x-2 mb-4">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <p className="text-sm font-medium text-green-700">
                      {uploadMessage}
                    </p>
                  </div>
                )}
                
                {uploadStatus === 'error' && (
                  <div className="flex items-center justify-center space-x-2 mb-4">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <p className="text-sm font-medium text-red-700">
                      {uploadMessage}
                    </p>
                  </div>
                )}

                {showSizeWarning && (
                  <div className="flex items-center justify-center space-x-2 text-yellow-600 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <span>Photo size is large ({(capturedPhoto.size / (1024 * 1024)).toFixed(1)}MB). Consider retaking for faster upload.</span>
                  </div>
                )}
                
                {/* Action Buttons */}
                {uploadStatus === 'none' && (
                  <div className="flex space-x-3 mt-4">
                    <button
                      type="button"
                      onClick={retakePhoto}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Retake
                    </button>
                    <button
                      type="button"
                      onClick={confirmPhoto}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Confirm & Upload
                    </button>
                  </div>
                )}
                
                {uploadStatus === 'error' && (
                  <div className="flex space-x-3 mt-4">
                    <button
                      type="button"
                      onClick={retakePhoto}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Retake
                    </button>
                    <button
                      type="button"
                      onClick={confirmPhoto}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Try Again
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Camera Interface */
            <>
              {/* Video Preview - Always rendered but conditionally visible */}
              <div className={`relative w-full h-full min-h-[400px] ${cameraState === 'active' ? 'block' : 'hidden'}`}>
                <video
                  ref={videoRef}
                  data-testid="video-preview"
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover rounded-lg"
                  style={{ minHeight: '400px' }}
                />
                
                {/* Camera Controls Overlay - Only show when active */}
                {cameraState === 'active' && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-4">
                    <button
                      type="button"
                      onClick={capturePhoto}
                      disabled={isLoading}
                      className={`
                        p-4 bg-white rounded-full border-4 border-blue-600 focus:ring-2 focus:ring-blue-500
                        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-50 active:scale-95'}
                      `}
                      aria-label="Capture photo"
                    >
                      <Camera className="h-6 w-6 text-blue-600" />
                    </button>
                    
                    <button
                      type="button"
                      onClick={stopCamera}
                      disabled={isLoading}
                      className={`
                        px-4 py-2 bg-gray-600 text-white rounded-lg font-medium
                        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-700 focus:ring-2 focus:ring-gray-500'}
                      `}
                    >
                      Stop Camera
                    </button>
                  </div>
                )}
              </div>

              {/* Camera Starting */}
              {cameraState === 'starting' && (
                <div className="flex flex-col items-center">
                  <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-4" />
                  <p className="text-lg font-medium text-gray-700">Starting camera...</p>
                  <p className="text-sm text-gray-500">Please allow camera access when prompted</p>
                </div>
              )}

              {/* Camera Error */}
              {cameraState === 'error' && error && (
                <div className="text-center p-6">
                  <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{error.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{error.message}</p>
                  <button
                    type="button"
                    onClick={handleRetry}
                    disabled={isLoading}
                    className={`
                      px-4 py-2 bg-blue-600 text-white rounded-lg font-medium
                      ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'}
                    `}
                  >
                    Try Again
                  </button>
                </div>
              )}

              {/* Camera Inactive */}
              {cameraState === 'inactive' && (
                <div className="text-center p-6">
                  {isLoading ? (
                    <div className="flex flex-col items-center">
                      <Loader2 data-testid="capture-loading-spinner" className="h-12 w-12 text-blue-500 animate-spin mb-4" />
                      <p className="text-lg font-medium text-gray-700">Processing...</p>
                    </div>
                  ) : (
                    <>
                      <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-lg font-medium text-gray-700 mb-2">
                        Ready to take your selfie
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        Click the button below to start your camera
                      </p>
                      <button
                        type="button"
                        onClick={startCamera}
                        disabled={isLoading}
                        aria-describedby="camera-instructions"
                        className={`
                          px-6 py-3 bg-blue-600 text-white rounded-lg font-medium
                          ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'}
                        `}
                      >
                        Start Camera
                      </button>
                      <p id="camera-instructions" className="text-xs text-gray-500 mt-2">
                        You may be prompted to allow camera access
                      </p>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Hidden canvas for photo capture */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className={`
              px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500
              ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            Back
          </button>

          {/* Only show Next button if no photo captured (user should use Confirm & Upload instead) */}
          {!capturedPhoto && (
            <button
              type="submit"
              disabled={true}
              className="px-6 py-2 rounded-lg text-white font-medium bg-gray-400 cursor-not-allowed focus:ring-2 focus:ring-blue-500"
            >
              Take Photo First
            </button>
          )}
        </div>
      </form>
    </div>
  );
};